// app/src/features/telemetry/useTelemetryWiring.js
import { useEffect } from "react";
import { setUiContext } from "../../lib/logRingBuffer";
import { telemetryView } from "../../lib/telemetry/sanitize.js";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { usePanelToggleIntents } from "../../hooks/usePanelToggleIntents.js";
import { useForecastOpenIntent } from "../../hooks/useForecastOpenIntent.js";
import { useTelemetryAppState } from "../../hooks/useTelemetryAppState.js";

/**
 * The telemetry wiring, first point: `usePanelToggleIntents` (the `panel.toggle` intent at the
 * side panels' open-state transitions), then the bug-context stamping effect (the active
 * forecast's identity and the `view` mirrored into the log ring's ambient context). The order is
 * semantic and pinned by `__tests__/useTelemetryWiring.test.jsx`: a `panel.toggle` recorded in the
 * commit that opened a panel carries the PREVIOUS `view`. Returns nothing; the active forecast is
 * read through `useActiveForecast()` (nothing here imports the store).
 */
export function useTelemetryWiring({ activeTrace, logOpen, chatOpen, tsDrawerOpen, contractOpen }) {
  const active = useActiveForecast();
  usePanelToggleIntents({
    forecasts: logOpen,
    chat: chatOpen,
    question: contractOpen,
    signal_library: tsDrawerOpen,
  });
  // Mirror the active forecast's identity into the bug-report ring buffer's
  // ambient context. Every recordUiEvent fired afterwards is auto-stamped
  // with active_id / active_forecast_id / active_trace_id so triagers can
  // see which forecast the user was on at each event without us threading
  // the ids through every call site. The useEffect commits after the
  // render — there's a small window between the activeSet dispatch and this firing
  // where events recorded in the same handler see the previous active —
  // events that need precise from→to context (e.g. ui.forecast.switch)
  // pass the ids explicitly to override the ambient.
  useEffect(() => {
    // active_trace_id comes from the active row itself (active.traceId), NOT
    // activeTrace — activeTrace tracks the in-flight stream in *this tab*,
    // which can belong to a different forecast than `active` after the user
    // clicks a sibling running row. Sourcing from active.traceId keeps the
    // ambient context internally consistent (all three ids describe the same
    // row). The in-flight stream id is exposed separately as in_flight_trace_id
    // so triage can distinguish the row the user was viewing from the stream
    // their tab was actively driving.
    setUiContext({
      active_id: active?.id || null,
      active_forecast_id: active?.forecastId || null,
      active_trace_id: active?.traceId || null,
      in_flight_trace_id: activeTrace?.traceId || null,
      in_flight_forecast_id: activeTrace?.forecastId || null,
      contract_key: active?.contractKey ?? active?._obs?.contractKey ?? null,
      contract_key_status: active?.contractKeyStatus ?? active?._obs?.contractKeyStatus ?? null,
      view: telemetryView(window.location.pathname, {
        log: logOpen,
        chat: chatOpen,
        tsd: tsDrawerOpen,
        contract: contractOpen,
      }),
    });
  }, [
    logOpen,
    chatOpen,
    tsDrawerOpen,
    contractOpen,
    active?.id,
    active?.forecastId,
    active?.traceId,
    activeTrace?.traceId,
    activeTrace?.forecastId,
    active?.contractKey,
    active?.contractKeyStatus,
    active?._obs?.contractKey,
    active?._obs?.contractKeyStatus,
  ]);
}

/**
 * The telemetry wiring, second point: `useForecastOpenIntent` (the `forecast.open` intent at the
 * focus transition) as the first statement, then `useTelemetryAppState` (the app state P2
 * telemetry reads: the `tab.start` snapshot, the attention tracker's streaming runs). Ordering
 * contract, pinned by `__tests__/useTelemetryWiring.test.jsx`: `AppInner` calls this after
 * `useTelemetryWiring` (the bug-context stamping effect whose ambient `active_*` ids the open
 * intent is stamped with) and after `useUrlHydration` (whose URL-apply effects the app-state hook
 * requires before it); inside, the open intent registers before the app-state hook. The inputs
 * are the raw `AppInner` bindings the app-state hook's argument object is composed from. Returns
 * nothing: both registrations are side effects. The active forecast is read through
 * `useActiveForecast()`; nothing here imports the store.
 */
export function useTelemetryAppStateWiring({
  forecastsLoaded,
  sharedSettled,
  compareHydratedRef,
  forecastDeepLinkRef,
  forecasts,
  sharedForecasts,
  forecastEngine,
  mockToday,
  debugResolutionOverride,
  debugPremiseOverride,
  debugForceGenerating,
}) {
  const active = useActiveForecast();
  useForecastOpenIntent({ activeId: active?.id });
  // The state P2 telemetry reads (the tab.start snapshot, the attention tracker's streaming runs),
  // handed over once both lanes have loaded and the URL's view has been applied (the effects above).
  useTelemetryAppState({
    ready: forecastsLoaded && sharedSettled,
    isHydrated: () => !!(compareHydratedRef.current && forecastDeepLinkRef.current),
    active,
    forecasts,
    sharedForecasts,
    forecastEngine,
    mockToday,
    debugOverrides: {
      resolution: debugResolutionOverride,
      premise: debugPremiseOverride,
      generating: debugForceGenerating,
    },
  });
}
